import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage, routeMapping } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { heroImages } from '../data/mockData';
// Note: heroImages.secondary (Paris bridge) is only used on La Maison page
import SEOHead from '../components/SEOHead';

const HomeBilingual = () => {
  const { language, t } = useLanguage();
  const home = translations.home;
  const nav = translations.nav;
  
  const langPrefix = `/${language}`;
  const getRoute = (pageKey) => {
    const route = routeMapping[language][pageKey];
    return `${langPrefix}${route ? '/' + route : ''}`;
  };

  return (
    <div>
      <SEOHead 
        title={t(home.seoTitle)}
        description={t(home.seoDescription)}
      />
      
      {/* Hero Section */}
      <section className="section-padding-large" style={{ 
        background: 'var(--bg-primary)',
        display: 'flex',
        alignItems: 'center'
      }}>
        <div className="container">
          <div className="grid-two-column" style={{ gap: '80px', alignItems: 'center' }}>
            <div>
              <p className="meta-text" style={{ marginBottom: '24px', color: 'var(--gold-accent)' }}>
                {t(home.heroTagline)}
              </p>
              <h1 className="hero-large" style={{ marginBottom: '32px' }} dangerouslySetInnerHTML={{ __html: t(home.heroTitle) }} />
              <p className="body-large" style={{ 
                marginBottom: '40px',
                color: 'var(--text-secondary)',
                maxWidth: '500px'
              }}>
                {t(home.heroDescription)}
              </p>
              <Link to={getRoute('contact')} className="btn-primary" data-testid="hero-cta-btn">
                {t(nav.cta)}
              </Link>
            </div>
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_c17ab4df-56cb-4633-98aa-b5d05bd5d93e/artifacts/6y9t1fck_Pont%20Alexandre%20III.jpeg"
                alt="Fenicis Global Hospitality"
                style={{
                  width: '100%',
                  objectFit: 'contain',
                  clipPath: 'inset(0 1.5% 0 0)',
                  filter: 'none'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Expertise at a Glance Section */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <p className="meta-text" style={{ marginBottom: '16px', color: 'var(--gold-accent)' }}>
              {t(home.expertiseSubtitle)}
            </p>
            <h2 className="heading-1" style={{ marginBottom: '16px' }}>
              {t(home.expertiseTitle)}
            </h2>
          </div>
          
          <div className="expertise-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '32px',
            marginBottom: '48px'
          }}>
            <div style={{
              padding: '32px 24px',
              background: 'var(--bg-primary)',
              borderTop: '2px solid var(--gold-accent)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '12px', fontSize: '18px' }}>
                {t(home.pillar1Title)}
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', fontSize: '14px', textAlign: 'left' }}>
                {t(home.pillar1Desc)}
              </p>
            </div>
            
            <div style={{
              padding: '32px 24px',
              background: 'var(--bg-primary)',
              borderTop: '2px solid var(--gold-accent)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '12px', fontSize: '18px' }}>
                {t(home.pillar2Title)}
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', fontSize: '14px', textAlign: 'left' }}>
                {t(home.pillar2Desc)}
              </p>
            </div>
            
            <div style={{
              padding: '32px 24px',
              background: 'var(--bg-primary)',
              borderTop: '2px solid var(--gold-accent)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '12px', fontSize: '18px' }}>
                {t(home.pillar3Title)}
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', fontSize: '14px', textAlign: 'left' }}>
                {t(home.pillar3Desc)}
              </p>
            </div>
            
            <div style={{
              padding: '32px 24px',
              background: 'var(--bg-primary)',
              borderTop: '2px solid var(--gold-accent)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '12px', fontSize: '18px' }}>
                {t(home.pillar4Title)}
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)', fontSize: '14px', textAlign: 'left' }}>
                {t(home.pillar4Desc)}
              </p>
            </div>
          </div>
          
          <style>{`
            @media (max-width: 900px) {
              .expertise-grid {
                grid-template-columns: repeat(2, 1fr) !important;
              }
            }
            @media (max-width: 600px) {
              .expertise-grid {
                grid-template-columns: 1fr !important;
              }
            }
          `}</style>
          
          <div style={{ textAlign: 'center' }}>
            <Link to={getRoute('expertise')} className="btn-secondary" data-testid="explore-expertise-btn">
              {t(home.exploreExpertise)}
            </Link>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <p className="meta-text" style={{ marginBottom: '24px', color: 'var(--gold-accent)' }}>
              {t(home.introSubtitle)}
            </p>
            <h2 className="hero-medium" style={{ marginBottom: '32px' }}>
              {t(home.introTitle)}
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              {t(home.introDescription)}
            </p>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <p className="meta-text" style={{ marginBottom: '24px', color: 'var(--gold-accent)' }}>
                {t(home.philosophySubtitle)}
              </p>
              <h2 className="heading-1">
                {t(home.philosophyTitle)}
              </h2>
            </div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))',
              gap: '48px'
            }}>
              <div>
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>{t(home.pillarArt)}</h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  {t(home.pillarArtDesc)}
                </p>
              </div>
              
              <div>
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>{t(home.pillarTransform)}</h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  {t(home.pillarTransformDesc)}
                </p>
              </div>
              
              <div>
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>{t(home.pillarEmotion)}</h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  {t(home.pillarEmotionDesc)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
            <h2 className="heading-1" style={{ marginBottom: '24px' }}>
              {t(home.ctaTitle)}
            </h2>
            <p className="body-large" style={{ 
              marginBottom: '40px',
              color: 'var(--text-secondary)'
            }} dangerouslySetInnerHTML={{ __html: t(home.ctaDescription) }} />
            <Link to={getRoute('contact')} className="btn-primary" data-testid="cta-btn">
              {t(nav.cta)}
            </Link>
          </div>
        </div>
      </section>

      {/* SEO Editorial Block */}
      <section style={{ padding: '40px 0', background: 'var(--bg-secondary)' }}>
        <div className="container">
          <p style={{ 
            fontSize: '11px',
            lineHeight: 1.6,
            color: 'var(--text-light)',
            maxWidth: '800px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            {t(home.seoBlock)}
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomeBilingual;
